import { AlertCircle, CheckCircle, Loader2, RefreshCw, Trash2, X, XCircle } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import contractsV2Service, { ContractPriceItem } from "../services/contractsV2Service";
import exchangesService from "../services/exchangesService";
import httpService from "../services/httpService";
import jobService from "../services/JobService";
import { FactorEntry } from "../services/productFactorsService";
import marketDataService from "../services/marketDataService";
import { Contract, ContractUpsertInput, PriceSource } from "../types/contract";
import { ScheduleTimeRange } from "../types/exchange";
import { StockPriceAllSourceEntry, StockPriceHistoryBar } from "../types/marketData";
import { PRESET_REMARKS, parseRemarks, sanitizeRemarkChars } from "../utils/contractRemarks";
import { formatNumber } from "../utils/numberUtils";

function floor30(d: Date): Date {
  const out = new Date(d);
  out.setUTCMinutes(out.getUTCMinutes() < 30 ? 0 : 30, 0, 0);
  return out;
}

function ceil30(d: Date): Date {
  const out = new Date(d);
  if (out.getUTCSeconds() === 0 && out.getUTCMilliseconds() === 0 && out.getUTCMinutes() % 30 === 0) {
    return out;
  }
  out.setUTCMinutes(out.getUTCMinutes() < 30 ? 30 : 60, 0, 0);
  return out;
}

async function fetchAllContractPrices(conId: number, from: string, to: string): Promise<ContractPriceItem[]> {
  const allItems: ContractPriceItem[] = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const resp = await contractsV2Service.getContractPricesPage(conId, page, from, to);
    allItems.push(...resp.items);
    totalPages = resp.totalPages;
    page++;
  }
  return allItems;
}

interface ForexRateHistoryItem {
  datetimeUtc: string;
  rate: string;
}

interface ForexRateHistoryResponse {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: ForexRateHistoryItem[];
}

async function fetchAllForexRates(fromCurrency: string, toCurrency: string, from: string, to: string): Promise<ForexRateHistoryItem[]> {
  const allItems: ForexRateHistoryItem[] = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const params = new URLSearchParams();
    params.set("fromCurrency", fromCurrency);
    params.set("toCurrency", toCurrency);
    params.set("from", from);
    params.set("to", to);
    params.set("pageNumber", String(page));
    const resp = await httpService.get<ForexRateHistoryResponse>(`/v2/forex/rates?${params.toString()}`);
    allItems.push(...resp.data.items);
    totalPages = resp.data.totalPages;
    page++;
  }
  return allItems;
}

async function fetchExchangeMarketHours(ibkrCode: string, from: string): Promise<ScheduleTimeRange[]> {
  const exchange = await exchangesService.getByIbkrCode(ibkrCode);
  const schedule = await exchangesService.getSchedule(exchange.id, from);
  return schedule.marketHours;
}

function isWithinMarketHours(timestamp: string, marketHours: ScheduleTimeRange[]): boolean {
  const ts = new Date(timestamp).getTime();
  return marketHours.some((range) => {
    const start = new Date(range.startDatetimeUtc).getTime();
    const end = new Date(range.endDatetimeUtc).getTime();
    return ts >= start && ts <= end;
  });
}

async function fetchAllContractFactors(conId: number, from: string, to: string): Promise<FactorEntry[]> {
  const allItems: FactorEntry[] = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const resp = await contractsV2Service.getContractFactorsPage(conId, page, from, to);
    allItems.push(...resp.items);
    totalPages = resp.totalPages;
    page++;
  }
  return allItems;
}

interface LivePriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: Contract;
  onContractUpdated: () => void;
}

const LivePriceModal: React.FC<LivePriceModalProps> = ({
  isOpen,
  onClose,
  contract,
  onContractUpdated,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const historyRequestId = useRef(0);

  const [prices, setPrices] = useState<StockPriceAllSourceEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local verification state to reflect changes immediately
  const [isVerified, setIsVerified] = useState<boolean>(!!contract.verifiedAtUtc);
  const [verifiedDate, setVerifiedDate] = useState<string | null>(contract.verifiedAtUtc);

  // Editable fields
  const [priceSource, setPriceSource] = useState<string>(contract.priceSource || "");
  const [figiComposite, setFigiComposite] = useState(contract.figiComposite || "");
  const [eodhdTicker, setEodhdTicker] = useState(contract.eodhdTicker || "");
  const [eodhdCountry, setEodhdCountry] = useState(contract.eodhdCountry || "");
  const [yahooTickerName, setYahooTickerName] = useState(contract.yahooTickerName || "");

  // Remarks rendered as chips; backend stores a comma-separated string
  const [remarks, setRemarks] = useState<string[]>(parseRemarks(contract.remarks));
  const [remarkInput, setRemarkInput] = useState("");

  // Track if fields have been modified
  const [hasChanges, setHasChanges] = useState(false);

  // Refetch historical prices confirmation
  const [showRefetchConfirm, setShowRefetchConfirm] = useState(false);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [refetchError, setRefetchError] = useState<string | null>(null);
  const [refetchContract, setRefetchContract] = useState<Contract | null>(null);

  // Delete historical plutus prices confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteRange, setDeleteRange] = useState<{ from: string; to: string } | null>(null);

  // Historical price bars
  const [ibkrBars, setIbkrBars] = useState<StockPriceHistoryBar[]>([]);
  const [eodhdBars, setEodhdBars] = useState<StockPriceHistoryBar[]>([]);
  const [bloombergBars, setBloombergBars] = useState<StockPriceHistoryBar[]>([]);
  const [plutusPrices, setPlutusPrices] = useState<ContractPriceItem[]>([]);
  const [plutusFactors, setPlutusFactors] = useState<FactorEntry[]>([]);
  const [forexRates, setForexRates] = useState<Map<string, number> | "USD">(new Map());
  const [marketHours, setMarketHours] = useState<ScheduleTimeRange[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Reset state when contract changes
  useEffect(() => {
    if (isOpen) {
      setPriceSource(contract.priceSource || "");
      setFigiComposite(contract.figiComposite || "");
      setEodhdTicker(contract.eodhdTicker || "");
      setEodhdCountry(contract.eodhdCountry || "");
      setYahooTickerName(contract.yahooTickerName || "");
      setRemarks(parseRemarks(contract.remarks));
      setRemarkInput("");
      setIsVerified(!!contract.verifiedAtUtc);
      setVerifiedDate(contract.verifiedAtUtc);
      setHasChanges(false);
      setError(null);
      setPrices([]);
      setIbkrBars([]);
      setEodhdBars([]);
      setBloombergBars([]);
      setPlutusPrices([]);
      setPlutusFactors([]);
      setForexRates(new Map());
      setMarketHours([]);
      setHistoryError(null);
      setShowRefetchConfirm(false);
      setRefetchLoading(false);
      setRefetchError(null);
      setRefetchContract(null);
      setShowDeleteConfirm(false);
      setDeleteLoading(false);
      setDeleteError(null);
      setDeleteConfirmText("");
      setDeleteRange(null);
    }
  }, [contract, isOpen]);

  // Fetch prices from all sources via market-data API
  const fetchPrices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await marketDataService.getStockPricesAll([contract.conId]);
      const contractPrices = response[contract.conId] || [];
      setPrices(contractPrices);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch prices");
    } finally {
      setLoading(false);
    }
  }, [contract.conId]);

  // Fetch historical bars from all sources in parallel
  const fetchHistory = useCallback(async () => {
    const requestId = ++historyRequestId.current;
    setHistoryLoading(true);
    setHistoryError(null);
    const now = new Date();
    const tenDaysAgo = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000);
    const from = floor30(tenDaysAgo).toISOString();
    const to = ceil30(now).toISOString();
    const scheduleFrom = new Date(tenDaysAgo.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const isUsd = contract.currency.toUpperCase() === "USD";
    const [ibkrResult, eodhdResult, bloombergResult, plutusResult, factorsResult, forexResult, marketHoursResult] = await Promise.allSettled([
      marketDataService.getStockPriceHistory(contract.conId, "IBKR"),
      marketDataService.getStockPriceHistory(contract.conId, "EODHD"),
      marketDataService.getStockPriceHistory(contract.conId, "BLOOMBERG"),
      fetchAllContractPrices(contract.conId, from, to),
      fetchAllContractFactors(contract.conId, from, to),
      isUsd ? Promise.resolve([]) : fetchAllForexRates(contract.currency, "USD", from, to),
      fetchExchangeMarketHours(contract.primaryExchange || contract.exchange, scheduleFrom),
    ]);
    if (requestId !== historyRequestId.current) return;
    setIbkrBars(ibkrResult.status === "fulfilled" ? ibkrResult.value.bars || [] : []);
    setEodhdBars(eodhdResult.status === "fulfilled" ? eodhdResult.value.bars || [] : []);
    setBloombergBars(bloombergResult.status === "fulfilled" ? bloombergResult.value.bars || [] : []);
    setPlutusPrices(plutusResult.status === "fulfilled" ? plutusResult.value : []);
    setPlutusFactors(factorsResult.status === "fulfilled" ? factorsResult.value : []);
    if (isUsd) {
      setForexRates("USD");
    } else {
      const items = forexResult.status === "fulfilled" ? forexResult.value : [];
      const normalizeToMinute = (iso: string) => {
        const d = new Date(iso);
        d.setUTCSeconds(0, 0);
        return d.toISOString();
      };
      const map = new Map(items.map((item) => [normalizeToMinute(item.datetimeUtc), parseFloat(item.rate)]));
      setForexRates(map);
    }
    setMarketHours(marketHoursResult.status === "fulfilled" ? marketHoursResult.value : []);
    if (ibkrResult.status === "rejected" && eodhdResult.status === "rejected" && bloombergResult.status === "rejected") {
      setHistoryError("Failed to fetch history from all sources");
    }
    setHistoryLoading(false);
  }, [contract.conId, contract.exchange, contract.primaryExchange, contract.currency]);

  // Auto-fetch on open
  useEffect(() => {
    if (isOpen) {
      fetchPrices();
      fetchHistory();
    }
  }, [isOpen, fetchPrices, fetchHistory]);

  // Merge IBKR, EODHD, and Bloomberg bars by datetime for the history table.
  // Normalize datetimes to minute precision so bars from different sources align.
  const historyRows = useMemo(() => {
    const normalizeToMinute = (iso: string) => {
      const d = new Date(iso);
      d.setUTCSeconds(0, 0);
      return d.toISOString();
    };
    const ibkrMap = new Map(ibkrBars.map((b) => [normalizeToMinute(b.datetime), b.close]));
    const eodhdMap = new Map(eodhdBars.map((b) => [normalizeToMinute(b.datetime), b.close]));
    const bloombergMap = new Map(bloombergBars.map((b) => [normalizeToMinute(b.datetime), b.close]));
    const plutusMap = new Map(plutusPrices.map((p) => [normalizeToMinute(p.datetimeUtc), parseFloat(p.close)]));
    const plutusSourceMap = new Map(plutusPrices.map((p) => [normalizeToMinute(p.datetimeUtc), p.source]));
    const factorMap = new Map(plutusFactors.map((f) => [normalizeToMinute(f.t), typeof f.f === "number" ? f.f : parseFloat(String(f.f))]));
    const forexMap = forexRates === "USD" ? null : forexRates;
    const allDatetimes = new Set([
      ...ibkrMap.keys(), ...eodhdMap.keys(), ...bloombergMap.keys(),
      ...plutusMap.keys(), ...factorMap.keys(),
      ...(forexMap ? forexMap.keys() : []),
    ]);
    const sorted = Array.from(allDatetimes).sort().reverse();
    return sorted.map((dt) => {
      const ibkr = ibkrMap.get(dt) ?? null;
      const eodhd = eodhdMap.get(dt) ?? null;
      const bloomberg = bloombergMap.get(dt) ?? null;
      const plutus = plutusMap.get(dt) ?? null;
      const plutusSource = plutusSourceMap.get(dt) ?? null;
      const factor = factorMap.get(dt) ?? null;
      const forex = forexRates === "USD" ? 1.0 : (forexMap!.get(dt) ?? null);
      return { datetime: dt, ibkr, eodhd, bloomberg, plutus, plutusSource, factor, forex };
    });
  }, [ibkrBars, eodhdBars, bloombergBars, plutusPrices, plutusFactors, forexRates]);

  // Track changes to editable fields
  useEffect(() => {
    const changed =
      priceSource !== (contract.priceSource || "") ||
      figiComposite !== (contract.figiComposite || "") ||
      eodhdTicker !== (contract.eodhdTicker || "") ||
      eodhdCountry !== (contract.eodhdCountry || "") ||
      yahooTickerName !== (contract.yahooTickerName || "") ||
      remarks.join(",") !== parseRemarks(contract.remarks).join(",") ||
      sanitizeRemarkChars(remarkInput).trim() !== "";
    setHasChanges(changed);
  }, [priceSource, figiComposite, eodhdTicker, eodhdCountry, yahooTickerName, remarks, remarkInput, contract]);

  // Save ticker changes
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Commit any text still in the remark input so it is not lost on save
      const pendingRemark = sanitizeRemarkChars(remarkInput).trim();
      const finalRemarks = pendingRemark && !remarks.includes(pendingRemark) ? [...remarks, pendingRemark] : remarks;
      const input: ContractUpsertInput = {
        conId: contract.conId,
        symbol: contract.symbol,
        secType: contract.secType,
        currency: contract.currency,
        exchange: contract.exchange,
        primaryExchange: contract.primaryExchange,
        longName: contract.longName,
        priceSource: priceSource || null,
        figiComposite: figiComposite.trim() || null,
        eodhdTicker: eodhdTicker.trim() || null,
        eodhdCountry: eodhdCountry.trim() || null,
        yahooTickerName: yahooTickerName.trim() || null,
        remarks: finalRemarks.length > 0 ? finalRemarks.join(",") : null,
      };
      await contractsV2Service.upsertContract(input);
      setRemarks(finalRemarks);
      setRemarkInput("");
      onContractUpdated();
      setHasChanges(false);
      fetchPrices();
      fetchHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // Remark helpers -- enforce lowercase + allowed chars, de-duplicate, ignore empties
  const addRemark = (raw: string) => {
    const remark = sanitizeRemarkChars(raw).trim();
    if (!remark) return;
    setRemarks((prev) => (prev.includes(remark) ? prev : [...prev, remark]));
  };

  const removeRemark = (remark: string) => {
    setRemarks((prev) => prev.filter((r) => r !== remark));
  };

  const handleRemarkInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    if (value.includes(",")) {
      // Comma commits the preceding text; keep the trailing remainder in the input
      const segments = value.split(",");
      const remainder = segments.pop() ?? "";
      segments.forEach((seg) => addRemark(seg));
      setRemarkInput(sanitizeRemarkChars(remainder));
    } else {
      setRemarkInput(sanitizeRemarkChars(value));
    }
  };

  const handleRemarkInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addRemark(remarkInput);
      setRemarkInput("");
    } else if (e.key === "Backspace" && remarkInput === "" && remarks.length > 0) {
      setRemarks((prev) => prev.slice(0, -1));
    }
  };

  // Toggle verification status
  const handleToggleVerification = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isVerified) {
        await contractsV2Service.clearVerifications([contract.conId]);
        setIsVerified(false);
        setVerifiedDate(null);
      } else {
        await contractsV2Service.setVerifications([contract.conId]);
        setIsVerified(true);
        setVerifiedDate(new Date().toISOString());
      }
      onContractUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update verification");
    } finally {
      setSaving(false);
    }
  };

  // Open refetch confirmation -- re-fetches the contract to get latest data
  const handleOpenRefetchConfirm = async () => {
    setRefetchError(null);
    try {
      const results = await contractsV2Service.findContractsByConIds([contract.conId]);
      if (!results.length) throw new Error("Contract not found");
      setRefetchContract(results[0]);
      setShowRefetchConfirm(true);
    } catch (err) {
      setRefetchError(err instanceof Error ? err.message : "Failed to load contract");
      setShowRefetchConfirm(true);
    }
  };

  const handleRefetchOverwrite = async () => {
    setRefetchLoading(true);
    setRefetchError(null);
    try {
      await jobService.runJob("historical-prices-job", [
        { name: "OVERRIDE_CON_IDS", value: String(contract.conId) },
      ]);
      setShowRefetchConfirm(false);
    } catch (err) {
      setRefetchError(err instanceof Error ? err.message : "Failed to trigger job");
    } finally {
      setRefetchLoading(false);
    }
  };

  // Open delete confirmation -- window is now minus 9 days through now (UTC)
  const handleOpenDeleteConfirm = () => {
    const toMidnightIso = (d: Date) => {
      const m = new Date(d);
      m.setUTCHours(0, 0, 0, 0);
      return m.toISOString().replace(/\.\d{3}Z$/, "Z");
    };
    const toIso = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, "Z");
    const now = new Date();
    const nineDaysAgo = new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000);
    setDeleteRange({ from: toMidnightIso(nineDaysAgo), to: toIso(now) });
    setDeleteConfirmText("");
    setDeleteError(null);
    setShowDeleteConfirm(true);
  };

  const handleDeletePrices = async () => {
    if (!deleteRange) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await contractsV2Service.deleteContractPrices(contract.conId, deleteRange.from, deleteRange.to);
      setShowDeleteConfirm(false);
      fetchHistory();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete prices");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Get price for a specific source
  const getPriceForSource = (source: PriceSource): StockPriceAllSourceEntry | undefined => {
    return prices.find((p) => p.source === source);
  };

  if (!isOpen) return null;

  const inputClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDarkMode
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;

  const renderPriceRow = (source: PriceSource, label: string) => {
    const info = getPriceForSource(source);

    return (
      <tr
        key={source}
        className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} last:border-0`}
      >
        <td className="py-3 pr-4">
          <div className="flex items-center gap-2">
            <span className={`font-medium ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}>
              {label}
            </span>
            {info && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${
                  info.isSourceMapped
                    ? isDarkMode
                      ? "bg-green-900/30 text-green-400"
                      : "bg-green-100 text-green-700"
                    : isDarkMode
                      ? "bg-yellow-900/30 text-yellow-400"
                      : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {info.isSourceMapped ? "Mapped" : "Not Mapped"}
              </span>
            )}
          </div>
        </td>
        <td className="py-3 text-right">
          {loading ? (
            <Loader2 className={`w-4 h-4 animate-spin inline ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
          ) : info ? (
            <span className={`font-mono ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}>
              {formatNumber(info.price)}
            </span>
          ) : (
            <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{"\u2014"}</span>
          )}
        </td>
      </tr>
    );
  };

  const formatHistoryDatetime = (iso: string) => {
    const d = new Date(iso);
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    const hours = String(d.getUTCHours()).padStart(2, "0");
    const minutes = String(d.getUTCMinutes()).padStart(2, "0");
    return `${month}/${day} ${hours}:${minutes}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div>
              <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                {contract.symbol}
              </h2>
              <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {contract.longName}
              </p>
            </div>
            <button
              onClick={() => { fetchPrices(); fetchHistory(); }}
              disabled={loading || historyLoading}
              className={`text-sm flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                isDarkMode
                  ? "text-blue-400 hover:bg-gray-700"
                  : "text-blue-600 hover:bg-gray-100"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading || historyLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-xs px-2 py-1 rounded-full font-medium ${
                isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
              }`}
            >
              {contract.currency}
            </span>
            <button
              onClick={onClose}
              className={`p-1 rounded-full transition-colors ${
                isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Two-column content */}
        <div className="flex-1 overflow-hidden flex min-h-0">
          {/* Left panel -- existing content */}
          <div className={`w-1/2 overflow-y-auto p-4 space-y-6 border-r ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Verification Status */}
            <div
              className={`p-3 rounded-lg flex items-center justify-between ${
                isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-2">
                {isVerified ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className={`font-medium ${isDarkMode ? "text-green-400" : "text-green-700"}`}>
                      Verified
                    </span>
                    {verifiedDate && (
                      <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {new Date(verifiedDate).toLocaleDateString()}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-gray-400" />
                    <span className={`font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Not Verified
                    </span>
                  </>
                )}
              </div>
              <button
                onClick={handleToggleVerification}
                disabled={saving}
                className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                  isVerified
                    ? isDarkMode
                      ? "bg-red-900/30 text-red-400 hover:bg-red-900/50"
                      : "bg-red-100 text-red-700 hover:bg-red-200"
                    : isDarkMode
                      ? "bg-green-900/30 text-green-400 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                } disabled:opacity-50`}
              >
                {saving ? "..." : isVerified ? "Mark Unverified" : "Mark Verified"}
              </button>
            </div>

            {/* Price Source */}
            <div>
              <h3
                className={`text-sm font-medium uppercase tracking-wider mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Price Source
              </h3>
              <select
                value={priceSource}
                onChange={(e) => setPriceSource(e.target.value)}
                className={inputClass}
              >
                <option value="">None</option>
                <option value="EODHD">EODHD</option>
                <option value="IBKR">IBKR</option>
              </select>
            </div>

            {/* Editable Ticker Fields */}
            <div>
              <h3
                className={`text-sm font-medium uppercase tracking-wider mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Ticker Mappings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelClass}>FIGI Composite</label>
                  <input
                    type="text"
                    value={figiComposite}
                    onChange={(e) => setFigiComposite(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., BBG000B9XRY4"
                  />
                </div>
                <div>
                  <label className={labelClass}>EODHD Ticker</label>
                  <input
                    type="text"
                    value={eodhdTicker}
                    onChange={(e) => setEodhdTicker(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., AAPL"
                  />
                </div>
                <div>
                  <label className={labelClass}>EODHD Country</label>
                  <input
                    type="text"
                    value={eodhdCountry}
                    onChange={(e) => setEodhdCountry(e.target.value.toUpperCase())}
                    className={inputClass}
                    placeholder="e.g., US"
                  />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Yahoo Ticker</label>
                  <input
                    type="text"
                    value={yahooTickerName}
                    onChange={(e) => setYahooTickerName(e.target.value)}
                    className={inputClass}
                    placeholder="e.g., AAPL"
                  />
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div>
              <h3
                className={`text-sm font-medium uppercase tracking-wider mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Remarks
              </h3>
              <div
                className={`flex flex-wrap items-center gap-2 px-3 py-2 border rounded-md focus-within:ring-2 focus-within:ring-rp-amber ${
                  isDarkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                }`}
              >
                {remarks.map((remark) => (
                  <span
                    key={remark}
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${
                      isDarkMode ? "bg-rp-amber/20 text-rp-amber" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {remark}
                    <button
                      type="button"
                      onClick={() => removeRemark(remark)}
                      aria-label={`Remove ${remark}`}
                      className="hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={remarkInput}
                  onChange={handleRemarkInputChange}
                  onKeyDown={handleRemarkInputKeyDown}
                  onBlur={() => { if (remarkInput.trim()) { addRemark(remarkInput); setRemarkInput(""); } }}
                  placeholder={remarks.length === 0 ? "Add remark, comma to commit" : ""}
                  className={`flex-1 min-w-[8rem] bg-transparent text-sm focus:outline-none ${
                    isDarkMode ? "text-white placeholder-gray-400" : "text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>
              <p className={`mt-1 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                Lowercase letters, numbers and hyphen. Comma adds a remark.
              </p>
              {PRESET_REMARKS.some((preset) => !remarks.includes(preset)) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    Suggested
                  </span>
                  {PRESET_REMARKS.filter((preset) => !remarks.includes(preset)).map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => addRemark(preset)}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border transition-colors ${
                        isDarkMode
                          ? "border-gray-600 text-gray-300 hover:border-rp-amber hover:text-rp-amber"
                          : "border-gray-300 text-gray-600 hover:border-rp-amber hover:text-rp-amber"
                      }`}
                    >
                      + {preset}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Save/Cancel buttons */}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"
                } disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>

            {/* Divider */}
            <hr className={isDarkMode ? "border-gray-700" : "border-gray-200"} />

            {/* Live Prices */}
            <div>
              <h3
                className={`text-sm font-medium uppercase tracking-wider mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Live Prices From Source
              </h3>
              <table className="w-full">
                <thead>
                  <tr
                    className={`text-xs uppercase tracking-wider ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    <th className="text-left py-2">Source</th>
                    <th className="text-right py-2">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {renderPriceRow("BLOOMBERG", "Bloomberg")}
                  {renderPriceRow("EODHD", "EODHD")}
                  {renderPriceRow("IBKR", "IBKR")}
                  {renderPriceRow("YAHOO", "Yahoo")}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right panel -- Historical Prices */}
          <div className="w-1/2 overflow-y-auto p-4 flex flex-col min-h-0">

            {historyError && (
              <div className="p-3 rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-400 flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {historyError}
              </div>
            )}

            {historyLoading && historyRows.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className={`w-6 h-6 animate-spin ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
              </div>
            ) : historyRows.length === 0 ? (
              <div className={`text-sm text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                No historical data available
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto relative">
                {historyLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-10">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="sticky top-0">
                    <tr
                      className={`text-xs uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500 bg-gray-800" : "text-gray-400 bg-white"
                      }`}
                    >
                      <th rowSpan={2} className="text-left py-1 pr-2 align-bottom pb-2">Datetime (UTC)</th>
                      <th colSpan={3} className={`text-center py-1 px-2 border-b ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}>Historical From Source</th>
                      <th className={`w-px px-0 rowSpan={2} ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />
                      <th colSpan={3} className={`text-center py-1 px-2 border-b ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}>
                        <div className="flex items-center justify-center gap-2">
                          <span>Historical In Plutus</span>
                          <button
                            onClick={handleOpenRefetchConfirm}
                            className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                              isDarkMode
                                ? "bg-rp-amber/20 text-rp-amber hover:bg-rp-amber/30"
                                : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            }`}
                          >
                            Refetch
                          </button>
                        </div>
                      </th>
                    </tr>
                    <tr
                      className={`text-xs uppercase tracking-wider ${
                        isDarkMode ? "text-gray-500 bg-gray-800" : "text-gray-400 bg-white"
                      }`}
                    >
                      <th className="text-right py-1 px-2 pb-2">Bloomberg</th>
                      <th className="text-right py-1 px-2 pb-2">EODHD</th>
                      <th className="text-right py-1 px-2 pb-2">IBKR</th>
                      <th className={`w-px px-0 ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />
                      <th className="text-right py-1 px-2 pb-2">
                        <div className="flex items-center justify-end gap-1">
                          <span>Prices</span>
                          <button
                            onClick={handleOpenDeleteConfirm}
                            title="Delete historical prices"
                            className={`p-0.5 rounded transition-colors ${
                              isDarkMode
                                ? "text-red-400 hover:bg-red-900/30"
                                : "text-red-600 hover:bg-red-100"
                            }`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </th>
                      <th className="text-right py-1 px-2 pb-2">Forex</th>
                      <th className="text-right py-1 pl-2 pb-2">Factor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((row) => {
                      const inMarket = marketHours.length > 0 && isWithinMarketHours(row.datetime, marketHours);
                      const cellColor = inMarket
                        ? isDarkMode ? "text-blue-400" : "text-blue-600"
                        : isDarkMode ? "text-gray-200" : "text-gray-900";
                      const dtColor = inMarket
                        ? isDarkMode ? "text-blue-400" : "text-blue-600"
                        : isDarkMode ? "text-gray-300" : "text-gray-700";
                      return (
                      <tr
                        key={row.datetime}
                        className={`border-b ${isDarkMode ? "border-gray-700/50" : "border-gray-100"} last:border-0`}
                      >
                        <td className={`py-1.5 pr-2 font-mono text-xs ${dtColor}`}>
                          {formatHistoryDatetime(row.datetime)}
                        </td>
                        <td className={`py-1.5 px-2 text-right font-mono ${cellColor}`}>
                          {row.bloomberg !== null ? formatNumber(row.bloomberg) : "—"}
                        </td>
                        <td className={`py-1.5 px-2 text-right font-mono ${cellColor}`}>
                          {row.eodhd !== null ? formatNumber(row.eodhd) : "—"}
                        </td>
                        <td className={`py-1.5 px-2 text-right font-mono ${cellColor}`}>
                          {row.ibkr !== null ? formatNumber(row.ibkr) : "—"}
                        </td>
                        <td className={`w-px px-0 ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />
                        <td className={`py-1.5 px-2 text-right font-mono ${cellColor}`}>
                          {row.plutus !== null ? (
                            <>
                              {formatNumber(row.plutus)}
                              {row.plutusSource ? (
                                <span
                                  className={`ml-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                  title={row.plutusSource}
                                >
                                  {row.plutusSource.charAt(0).toUpperCase()}
                                </span>
                              ) : null}
                            </>
                          ) : "—"}
                        </td>
                        <td className={`py-1.5 px-2 text-right font-mono ${cellColor}`}>
                          {row.forex !== null ? row.forex.toFixed(4) : "—"}
                        </td>
                        <td className={`py-1.5 pl-2 text-right font-mono ${cellColor}`}>
                          {row.factor !== null ? row.factor.toFixed(6) : "—"}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Refetch Historical Prices Confirmation Dialog */}
      {showRefetchConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-lg shadow-xl max-w-lg w-full p-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Overwrite Historical Prices
            </h3>
            {refetchError && (
              <div className="p-3 rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-400 flex items-center gap-2 mb-4">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {refetchError}
              </div>
            )}
            {refetchContract && (
              <>
                <p className={`text-sm mb-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  Are you sure you would like to overwrite the historical prices in plutus for contract ({contract.conId}, {contract.symbol}, {contract.longName})?
                  The prices will be pulled from <span className="font-semibold">{refetchContract.priceSource || "N/A"}</span>.
                </p>
                <p className={`text-sm mb-6 ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`}>
                  This operation will take up to 3 mins to complete. It is recommended to do this operation after the current factors have been computed and before the next slot factor computation starts.
                </p>
              </>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowRefetchConfirm(false); setRefetchError(null); }}
                disabled={refetchLoading}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"
                } disabled:opacity-50`}
              >
                Cancel
              </button>
              {refetchContract && (
                <button
                  onClick={handleRefetchOverwrite}
                  disabled={refetchLoading}
                  className="px-4 py-2 bg-rp-amber hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {refetchLoading ? "Triggering..." : "Overwrite"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Historical Prices Confirmation Dialog */}
      {showDeleteConfirm && deleteRange && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-lg shadow-xl max-w-lg w-full p-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Delete Historical Prices
            </h3>
            {deleteError && (
              <div className="p-3 rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-400 flex items-center gap-2 mb-4">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {deleteError}
              </div>
            )}
            <p className={`text-sm mb-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              This will permanently delete the contract prices in plutus for ({contract.conId}, {contract.symbol}, {contract.longName}) from{" "}
              <span className="font-mono font-semibold">{deleteRange.from}</span> to{" "}
              <span className="font-mono font-semibold">{deleteRange.to}</span>. This action cannot be undone.
            </p>
            <label className={`block text-sm mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              Type <span className="font-semibold">yes</span> to confirm:
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              disabled={deleteLoading}
              placeholder="yes"
              className={`w-full mb-6 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
              }`}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                disabled={deleteLoading}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"
                } disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePrices}
                disabled={deleteLoading || deleteConfirmText.trim().toLowerCase() !== "yes"}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LivePriceModal;
